import { useMutation } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'

import { ERROR_MESSAGES } from '~/constants/messages'
import useHandleError from '~/hooks/useHandleError'
import useRequestTicket from '~/hooks/useRequestTicket'
import pickup from '~/services/pickup'
import { rxNumber } from '~/utils/regex'

import Button from '../common/Button'
import Input from '../common/Input'
import Modal from '../common/Modal'
import SignaturePad, { SignaturePadHandle } from './SignaturePad'

interface ModalSuratKuasaVerifyProps {
  isOpen: boolean
  closeModal: () => void
  kodeUnik: string
  locationId: string
  necessaryId: string
  prefillPhone?: string
  onDone: () => void
}

type Step = 'ktp' | 'signature'

// Extra steps for the "Dikuasakan" (Surat Kuasa) pickup flow, shown between
// validate-code and the queue ticket: verify the penerima kuasa's KTP last 3
// digits (the person physically at the counter), then in one screen capture
// their signature + consent + WhatsApp number and get the queue ticket right
// away - no separate ModalPhone step for this flow.
const ModalSuratKuasaVerify = ({
  isOpen,
  closeModal,
  kodeUnik,
  locationId,
  necessaryId,
  prefillPhone,
  onDone,
}: ModalSuratKuasaVerifyProps) => {
  const handleError = useHandleError()
  const { requestTicket, isLoading: isRequestingTicket } = useRequestTicket({ locationId, necessaryId, kodeUnik })
  const [step, setStep] = useState<Step>('ktp')
  const [hasStroke, setHasStroke] = useState(false)
  const signaturePadRef = useRef<SignaturePadHandle>(null)

  const {
    register,
    handleSubmit,
    reset: resetKtpForm,
    formState: { errors },
  } = useForm()

  const {
    register: registerSignatureForm,
    handleSubmit: handleSubmitSignatureForm,
    setValue: setSignatureFormValue,
    reset: resetSignatureForm,
    formState: { errors: signatureFormErrors },
  } = useForm()

  // Same as ModalPhone's prefill - this component stays mounted the whole
  // time isOpen toggles, so defaultValues alone won't pick up a later change.
  useEffect(() => {
    if (isOpen && prefillPhone) {
      setSignatureFormValue('phone', prefillPhone)
    }
  }, [isOpen, prefillPhone, setSignatureFormValue])

  const verifyKtpMutation = useMutation({
    mutationFn: pickup.verifyKtpDigit,
    onError: handleError,
    onSuccess: (res) => {
      if (res.data.valid) {
        setStep('signature')
      }
    },
  })

  const submitSignatureMutation = useMutation({
    mutationFn: pickup.submitSignature,
    onError: handleError,
  })

  const handleReset = () => {
    setStep('ktp')
    setHasStroke(false)
    resetKtpForm()
    resetSignatureForm()
    signaturePadRef.current?.clear()
  }

  const handleCloseModal = () => {
    closeModal()
    handleReset()
  }

  const onSubmitKtp: SubmitHandler<FieldValues> = (data) => {
    verifyKtpMutation.mutate({ kode_unik: kodeUnik, ktp_last_digit: (data.ktp_last_digit as string) ?? '' })
  }

  // One submit does all 3 remaining steps at once: save the signature, request
  // the queue ticket (redeeming the kode_unik along the way), then close out -
  // no intermediate "Selesaikan 1 langkah lagi" popup for this flow.
  const onSubmitSignature: SubmitHandler<FieldValues> = async (data) => {
    const blob = await signaturePadRef.current?.toBlob()
    if (!blob) {
      toast.error('Tanda tangan penerima kuasa wajib diisi')
      return
    }
    try {
      await submitSignatureMutation.mutateAsync({ kode_unik: kodeUnik, signature: blob })
      await requestTicket(data.phone as string)
      handleReset()
      onDone()
    } catch {
      // Errors are already toasted by submitSignatureMutation/requestTicket's
      // own onError - stay on this screen so the customer can retry.
    }
  }

  const isSubmittingSignature = submitSignatureMutation.isLoading || isRequestingTicket

  return (
    <Modal isOpen={isOpen} closeModal={handleCloseModal} showCloseButton={false}>
      {step === 'ktp' && (
        <form onSubmit={handleSubmit(onSubmitKtp)}>
          <div className="mb-2 text-2xl font-bold text-jba-blue">Verifikasi Identitas</div>
          <div className="mb-8 text-xs text-gray-500">
            Masukkan 3 digit terakhir nomor KTP penerima kuasa untuk melanjutkan.
          </div>
          <div className="mb-8">
            <Input
              type="number"
              rounded="xl"
              placeholder="3 digit terakhir KTP"
              errorMessage={errors.ktp_last_digit?.message as string}
              {...register('ktp_last_digit', {
                required: ERROR_MESSAGES.REQUIRED_KTP_DIGIT,
                minLength: { value: 3, message: 'Wajib 3 digit' },
                maxLength: { value: 3, message: 'Wajib 3 digit' },
              })}
            />
          </div>
          <Button type="submit" variant="primary" block rounded="xl" isLoading={verifyKtpMutation.isLoading}>
            {verifyKtpMutation.isLoading ? 'Memeriksa...' : 'Lanjutkan'}
          </Button>
        </form>
      )}

      {step === 'signature' && (
        <form onSubmit={handleSubmitSignatureForm(onSubmitSignature)}>
          <div className="mb-2 text-2xl font-bold text-jba-blue">Tanda Tangan Penerima Kuasa</div>
          <div className="mb-4 text-xs text-gray-500">Silakan tanda tangan pada kotak di bawah ini.</div>
          <div className="mb-2">
            <SignaturePad ref={signaturePadRef} onChange={setHasStroke} />
          </div>
          <div className="mb-4 text-right">
            <button
              type="button"
              className="text-xs text-jba-blue underline"
              onClick={() => signaturePadRef.current?.clear()}
            >
              Ulangi
            </button>
          </div>

          <label className="mb-6 flex items-start gap-2 text-xs text-gray-500">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 accent-jba-blue"
              {...registerSignatureForm('consent', { required: ERROR_MESSAGES.REQUIRED_CONSENT })}
            />
            Saya menyetujui penyerahan data diri saya untuk pengambilan unit / dokumen.
          </label>
          {signatureFormErrors.consent && (
            <div className="-mt-4 mb-4 text-xs text-red-500">{signatureFormErrors.consent.message as string}</div>
          )}

          <div className="mb-8">
            <Input
              type="number"
              rounded="xl"
              icon="mdi-whatsapp"
              placeholder="Nomor whatsapp yang dapat dihubungi"
              errorMessage={signatureFormErrors.phone?.message as string}
              {...registerSignatureForm('phone', {
                required: ERROR_MESSAGES.REQUIRED_PHONE,
                pattern: {
                  value: rxNumber,
                  message: ERROR_MESSAGES.VALID_NUMBER,
                },
                minLength: {
                  value: 10,
                  message: ERROR_MESSAGES.MINIMUM_10,
                },
                maxLength: {
                  value: 15,
                  message: ERROR_MESSAGES.MAXIMUM_15,
                },
              })}
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            block
            rounded="xl"
            disabled={!hasStroke}
            isLoading={isSubmittingSignature}
          >
            {isSubmittingSignature ? 'Mengambil antrean...' : 'Ambil Antrean'}
          </Button>
        </form>
      )}
    </Modal>
  )
}

export default ModalSuratKuasaVerify
